/**
 * Main service for exporting lessons to PowerPoint format
 */

// @ts-ignore - pptxgenjs types may not be available until package is installed
import pptxgen from 'pptxgenjs';
import { Lesson } from '../types';
import {
  createTitleSlide,
  createObjectivesSlide,
  createSuccessCriteriaSlide,
  createStandardsSlide,
  createMaterialsSlide,
  createTimelineStepSlides,
  createAssessmentSlides,
  createMisconceptionsSlide,
  createNotesSlide,
  ExportOptions
} from './powerpointTemplates';

// Re-export ExportOptions for external use
export type { ExportOptions };

const DEFAULT_OPTIONS: ExportOptions = {
  includeResources: true,
  includeAssessments: true,
  includeNotes: true,
  detailLevel: 'complete',
  theme: 'light'
};

/**
 * Exports a lesson to PowerPoint format
 * @param lesson The lesson to export
 * @param options Export options
 * @returns A pptxgen instance ready to be saved
 */
export async function exportLessonToPowerPoint(
  lesson: Lesson,
  options: ExportOptions = {}
): Promise<pptxgen> {
  const exportOptions = { ...DEFAULT_OPTIONS, ...options };
  
  // Create new presentation
  const pptx = new pptxgen();
  
  // Set presentation properties according to PowerPoint standards
  pptx.author = 'Education Application';
  pptx.company = 'DjammaDev';
  
  // Sanitize title and subject to prevent issues
  const safeTitle = (lesson.metadata.title || 'Untitled Lesson')
    .replace(/[^\x20-\x7E\u00A0-\uFFFF]/g, '') // Remove control chars but keep Unicode
    .substring(0, 255) || 'Untitled Lesson';
  pptx.title = safeTitle;
  
  const safeSubject = (lesson.metadata.subject || '')
    .replace(/[^\x20-\x7E\u00A0-\uFFFF]/g, '')
    .substring(0, 255) || '';
  if (safeSubject) {
    pptx.subject = safeSubject;
  }
  
  // Revision should be "1" (not version number)
  pptx.revision = '1';
  
  // Set slide size (16:9 widescreen) - use the standard layout
  // pptxgenjs 4.0.1 supports 'LAYOUT_WIDE' for 16:9 aspect ratio
  pptx.layout = 'LAYOUT_WIDE';
  
  try {
    // Slide 1: Title
    createTitleSlide(lesson, pptx, exportOptions);
    
    // Slide 2: Objectives
    if (lesson.objectives && lesson.objectives.length > 0) {
      createObjectivesSlide(lesson, pptx, exportOptions);
    }
    
    // Slide 3: Success Criteria
    if (lesson.success_criteria && lesson.success_criteria.length > 0) {
      createSuccessCriteriaSlide(lesson, pptx, exportOptions);
    }
    
    // Slide 4: Standards and Prerequisites
    if ((lesson.standards_alignment && lesson.standards_alignment.length > 0) || lesson.prerequisites) {
      createStandardsSlide(lesson, pptx, exportOptions);
    }
    
    // Slide 5: Materials
    if ((lesson.materials.digital && lesson.materials.digital.length > 0) ||
        (lesson.materials.physical_alternatives && lesson.materials.physical_alternatives.length > 0)) {
      createMaterialsSlide(lesson, pptx, exportOptions);
    }
    
    // Slides 6+: Timeline Steps
    if (lesson.timeline && lesson.timeline.length > 0) {
      for (let index = 0; index < lesson.timeline.length; index++) {
        await createTimelineStepSlides(lesson.timeline[index], index, lesson, pptx, exportOptions);
      }
    }
    
    // Assessment Slides
    if (exportOptions.includeAssessments) {
      createAssessmentSlides(lesson, pptx, exportOptions);
    }
    
    // Misconceptions and Differentiation
    createMisconceptionsSlide(lesson, pptx, exportOptions);
    
    // Notes Slide
    if (exportOptions.includeNotes) {
      createNotesSlide(lesson, pptx, exportOptions);
    }
    
    return pptx;
  } catch (error) {
    console.error('Error creating PowerPoint:', error);
    throw new Error(`Failed to export lesson to PowerPoint: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Exports a lesson to PowerPoint and triggers download
 * @param lesson The lesson to export
 * @param options Export options
 * @param filename Optional filename (defaults to lesson title)
 */
/**
 * Exports a lesson to PowerPoint and triggers download
 * @param lesson The lesson to export
 * @param options Export options
 * @param filename Optional filename (defaults to lesson title)
 */
export async function exportAndDownloadLesson(
  lesson: Lesson,
  options: ExportOptions = {},
  filename?: string
): Promise<void> {
  try {
    const pptx = await exportLessonToPowerPoint(lesson, options);
    
    // Sanitize filename to prevent issues
    const sanitizeFilename = (name: string): string => {
      if (!name) return 'lesson';
      
      // Normalize Unicode characters (é -> e, etc.)
      let sanitized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      
      // Replace special characters with underscores
      sanitized = sanitized.replace(/[^a-z0-9\s\-_]/gi, '_');
      
      // Replace spaces and multiple underscores
      sanitized = sanitized.replace(/\s+/g, '_');
      sanitized = sanitized.replace(/_+/g, '_');
      
      // Remove leading/trailing underscores
      sanitized = sanitized.replace(/^_+|_+$/g, '');
      
      // Limit length and ensure non-empty
      sanitized = sanitized.substring(0, 200);
      
      return sanitized || 'lesson';
    };
    
    const finalFilename = filename || `${sanitizeFilename(lesson.metadata.title || 'lesson')}.pptx`;
    
    // Use writeFile directly - it's the most reliable method
    // Post-processing is disabled for now as it may cause compatibility issues
    await pptx.writeFile({ fileName: finalFilename });
  } catch (error) {
    console.error('Error exporting lesson:', error);
    throw error;
  }
}
